<script setup>
import { useForm } from "@/form";
import AppLink from "@Components/AppLink.vue";
import { inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { InformationCircleIcon, ShoppingCartIcon, MegaphoneIcon, ShareIcon, NewspaperIcon, MapPinIcon } from "@heroicons/vue/24/outline";
import Badge from "@Components/Badge.vue";
import { MagicIcon, YouTubeIcon } from "@Icons/index";
import { BrainCircuitIcon, EyeIcon, FileTextIcon } from "lucide-vue-next";
import { PrimaryButton } from "@Components/Button";

const { t: __ } = useI18n();

const globalRefs = inject("globalRefs");

const loaded = ref(false);
const projects = ref([]);

const iconByType = {
    informative: InformationCircleIcon,
    commercial: ShoppingCartIcon,
    discover: MegaphoneIcon,
    local: MapPinIcon,
    bulk: FileTextIcon,
    siloing: ShareIcon,
    news: NewspaperIcon,
    "news-now": NewspaperIcon,
    rss: NewspaperIcon,
    "rss-now": NewspaperIcon,
    sitemap: NewspaperIcon,
    url: NewspaperIcon,
    youtube: YouTubeIcon,
};

onMounted(async () => {
    projects.value = await useForm({ limit: 8 }).post("projects_latest");
    loaded.value = true;
});
</script>

<template>
    <div>
        <div class="flex justify-between items-center">
            <div class="space-y-1">
                <h2 class="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">{{ __("Mes projets Autopilot récents") }}</h2>
            </div>
        </div>

        <div class="mt-6">
            <div v-if="projects.length > 0" class="grid gap-3 2xl:grid-cols-4 lg:grid-cols-3 sm:grid-cols-2">
                <AppLink
                    :to="{ name: 'publications.index', params: { id: project.id } }"
                    v-for="project in projects"
                    :class="[
                        'flex flex-col rounded-lg overflow-hidden group',
                        'ring-inset focus:ring-2 focus:ring-inset',
                        'bg-gray-50 ring-gray-300 hover:bg-gray-100 focus:ring-gray-950',
                        'dark:bg-gray-900 dark:ring-gray-800 dark:hover:bg-gray-800 dark:focus:ring-gray-700',
                    ]">
                    <div class="relative flex-none">
                        <img
                            v-if="project.first_published_publication?.thumbnail"
                            :src="project.first_published_publication.thumbnail.src"
                            class="w-full h-36 object-cover" />
                        <div v-else class="w-full h-36 bg-gray-200 dark:bg-gray-800"></div>
                        <div
                            :class="[
                                'bg-black/30 group-hover:bg-black/10 dark:bg-black/50 dark:group-hover:bg-black/30 transition-all duration-300 absolute inset-0',
                            ]"></div>
                        <div class="absolute top-2 left-2">
                            <Badge color="real-black"> {{ project.metrics.success }} {{ __("sur") }} {{ project.metrics.total }} {{ __("publiées") }} </Badge>
                        </div>
                        <div class="absolute top-2 right-2">
                            <Badge v-if="project.is_complete" color="green">
                                {{ __("Complété") }}
                            </Badge>
                            <Badge v-else color="yellow">
                                {{ __("En cours") }}
                            </Badge>
                        </div>
                    </div>
                    <div class="flex-1">
                        <div class="p-4">
                            <div class="text-sm font-medium line-clamp-2 text-gray-900 dark:text-white">{{ project.name }}</div>
                        </div>
                        <div class="grid grid-cols-2">
                            <AppLink
                                @click.stop
                                :to="{ name: 'projects.edit', params: { id: project.id } }"
                                class="flex justify-center items-center gap-2 p-2 dark:hover:bg-gray-700 hover:bg-gray-200">
                                <MagicIcon class="w-4 h-4" />
                                {{ __("Modifier le projet") }}
                            </AppLink>
                            <AppLink
                                @click.stop
                                :to="{ name: 'publications.index', params: { id: project.id } }"
                                class="flex justify-center items-center gap-2 p-2 dark:hover:bg-gray-700 hover:bg-gray-200">
                                <EyeIcon class="w-4 h-4" />
                                {{ __("Voir le projet") }}
                            </AppLink>
                        </div>
                    </div>
                </AppLink>
            </div>
            <button
                @click="globalRefs.toolsMenu.open('autopilot')"
                v-else-if="loaded"
                type="button"
                class="w-full relative block p-8 text-center rounded-lg border-2 border-gray-300 border-dashed group dark:border-gray-700 dark:hover:border-white focus:border-gray-900 focus:outline-none hover:border-gray-900">
                <BrainCircuitIcon class="mx-auto w-10 h-10 text-gray-600 dark:text-gray-400" />
                <div class="mt-6 text-lg font-semibold text-gray-900 dark:text-white">{{ __("Aucun projet récent") }}</div>
                <div class="mt-3 text-sm text-gray-600 dark:text-gray-400">{{ __("Vous n'avez pas encore créé de projet.") }}</div>
                <div class="flex justify-center mt-6">
                    <PrimaryButton :icon="MagicIcon">
                        {{ __("Génération Autopilot") }}
                    </PrimaryButton>
                </div>
            </button>
        </div>
        <div v-if="projects.length >= 8" class="flex justify-end mt-3">
            <AppLink
                :to="{ name: 'projects.index' }"
                class="text-sm text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 hover:text-gray-700 hover:underline">
                {{ __("Voir tous les projets") }}
            </AppLink>
        </div>
    </div>
</template>
